import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Button,
  Alert,
  CloseButton,
} from "react-bootstrap";
import CustomImage from "../components/CustomImage";
import Loading from "../components/Loading";
import WaveformPlayer from "../components/WaveformPlayer";
import VariantDisplayFields from "../components/VariantDisplayFields";
import ComparisonVideo from "../components/ComparisonVideo";
import PlaybackModeToggle from "../components/PlaybackModeToggle";
import PromptPanel from "../components/PromptPanel";
import useVideoPlayback from "../hooks/useVideoPlayback";
import "./Survey.css";
import { authenticatedFetch } from "../helpFunctions/authFetch";
import { getEnvVariable } from "../helpFunctions/getEnvVariable";
import { shouldIgnoreSurveyShortcut } from "../helpFunctions/keyboardShortcuts";

const Survey = () => {
  const emptyState = {
    files: [],
    itemType: "",
    mediaName: "",
    caption: "",
    caption_error: "",
    variantDisplays: [],
    variantsID: [],
    selection: "",
  };
  const [prevState, setPrevState] = useState(emptyState);
  const [currentState, setCurrentState] = useState(emptyState);
  const [nextState, setNextState] = useState(emptyState);

  // Add a queue for prefetched items
  const [prefetchQueue, setPrefetchQueue] = useState([]);
  const [isPrefetching, setIsPrefetching] = useState(false);
  const prefetchSize = 3; // Number of items to keep in the prefetch queue

  // Add state to track initial loading
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState(0);

  const [alertMessage, setAlertMessage] = useState({});
  const [alertAnimation, setAlertAnimation] = useState(false);
  const [surveyTitle, setSurveyTitle] = useState("");
  const voteDetails = useRef({});
  const [isSend, setIsSend] = useState(true);
  const [isFetchingNext, setIsFetchingNext] = useState(false);
  const surveyId = useParams().surveyId; // Extract survey ID from URL
  const apiBaseUrl = getEnvVariable("API_BASE_URL");

  const navigate = useNavigate();

  const videoRefs = useRef([]); // Store refs to video elements
  const audioRefs = useRef([]); // Store refs to audio elements

  const videoPlayback = useVideoPlayback({
    videoRefs,
    isVideo: currentState.itemType === "video",
    files: currentState.files,
  });

  // Add new state for keyboard selection visual feedback
  const [keyboardSelected, setKeyboardSelected] = useState(null);

  // Whether this survey shows prompts at all. Once it does, the prompt slot
  // stays reserved for every pair so the media below it never shifts.
  const [surveyShowsPrompt, setSurveyShowsPrompt] = useState(false);

  // Helper function to determine if a specific audio index should show as playing
  const isAudioPlaying = (index) => {
    // Sequential playback takes priority
    if (sequentialPlayback.isActive && sequentialPlayback.currentIndex === index && sequentialPlayback.isPlaying) {
      return true;
    }
    // Individual playback
    if (!sequentialPlayback.isActive && individualPlayback.activeIndex === index && individualPlayback.isPlaying) {
      return true;
    }
    return false;
  };

  // Add state for sequential audio playback
  const [sequentialPlayback, setSequentialPlayback] = useState({
    isActive: false,
    currentIndex: 0, // 0 for left, 1 for right
    isPlaying: false,
    pausedPosition: 0 // Store position when paused
  });

  // Add state for individual audio playback tracking
  const [individualPlayback, setIndividualPlayback] = useState({
    activeIndex: -1, // Which audio is playing individually (-1 = none)
    isPlaying: false
  });

  const fetchSurveyTitle = async () => {
    try {
      const response = await authenticatedFetch(
        navigate,
        `${apiBaseUrl}SurveyTitle/${surveyId}`
      );
      const resJson = await response.json();
      if (response.ok) {
        setSurveyTitle(resJson.survey_title || "Which is better?");
      } else {
        throw new Error(resJson.message);
      }
    } catch (error) {
      console.error("Error fetching survey title:", error);
      setSurveyTitle("Which is better?");
    }
  };

  // Modified to fetch a single pair and return it
  const fetchPair = async () => {
    try {
      const response = await authenticatedFetch(
        navigate,
        `${apiBaseUrl}survey/${surveyId}`
      );
      const resJson = await response.json();
      if (!response.ok) {
        throw resJson;
      }

      return {
        files: resJson.files,
        itemType: resJson.file_type,
        mediaName: resJson.media_name,
        caption: resJson.caption || "",
        caption_error: resJson.caption_error || "",
        variantDisplays: resJson.variant_displays || [],
        variantsID: resJson.variants_id,
        selection: "",
      };
    } catch (error) {
      if (
        error.detail ===
        "ValueError('The survey must contain at least two active variants')"
      ) {
        setAlertMessage({
          type: "warning",
          header: "Survey Unavailable",
          msg: "This survey requires at least two active variants to proceed.",
        });
      } else {
        setAlertMessage({
          type: "danger",
          header: "Error",
          msg: `There has been a problem: ${error.detail}`,
        });
      }
      return null;
    }
  };

  // Function to fill the prefetch queue
  const fillPrefetchQueue = async () => {
    if (isPrefetching) return;

    setIsPrefetching(true);

    try {
      // Calculate how many items we need to fetch
      const itemsToFetch = prefetchSize - prefetchQueue.length;

      if (itemsToFetch <= 0) {
        setIsPrefetching(false);
        return;
      }

      // Fetch multiple items in parallel
      const fetchPromises = Array(itemsToFetch).fill().map(() => fetchPair());

      // Update progress as each fetch completes
      if (isInitialLoading) {
        let completedFetches = 0;
        const progressPromises = fetchPromises.map(promise =>
          promise.then(result => {
            completedFetches++;
            setLoadingProgress(Math.floor((completedFetches / itemsToFetch) * 100));
            return result;
          })
        );
        const results = await Promise.all(progressPromises);
        const validResults = results.filter(result => result !== null);
        setPrefetchQueue(prevQueue => [...prevQueue, ...validResults]);
      } else {
        const results = await Promise.all(fetchPromises);
        const validResults = results.filter(result => result !== null);
        setPrefetchQueue(prevQueue => [...prevQueue, ...validResults]);
      }
    } catch (error) {
      console.error("Error filling prefetch queue:", error);
    } finally {
      setIsPrefetching(false);
    }
  };

  // Initial setup - fetch title and preload queue
  useEffect(() => {
    fetchSurveyTitle();

    // Initial preloading
    const preloadQueue = async () => {
      setIsInitialLoading(true);
      setLoadingProgress(0);

      await fillPrefetchQueue();

      // Don't set the current state here - let the other useEffect handle it
      // to avoid double loading
    };

    preloadQueue();
  }, []);

  // Keep the prefetch queue filled
  useEffect(() => {
    if (prefetchQueue.length < prefetchSize && !isPrefetching && !isInitialLoading) {
      fillPrefetchQueue();
    }

    // If we've filled the queue during initial loading, we can start the survey
    if (isInitialLoading && prefetchQueue.length > 0 && currentState.itemType === "") {
      const firstItem = prefetchQueue[0];
      setPrefetchQueue(prevQueue => prevQueue.slice(1));
      setCurrentState({ ...firstItem, selection: "" });
      setIsInitialLoading(false);
    }
  }, [prefetchQueue, isPrefetching, isInitialLoading, currentState.itemType]);

  // Get the next item from the queue
  const getNextFromQueue = () => {
    if (prefetchQueue.length === 0) {
      return null;
    }

    const nextItem = prefetchQueue[0];
    setPrefetchQueue(prevQueue => prevQueue.slice(1));

    return nextItem;
  };

  // Function to stop all audio/video playback
  const stopAllPlayback = () => {
    if (videoRefs.current.length) {
      videoRefs.current.forEach((video) => {
        video.pause();
        video.currentTime = 0;
      });
    }
    if (audioRefs.current.length) {
      audioRefs.current.forEach((audio) => {
        audio.pause();
        audio.currentTime = 0;
      });
    }
  };

  // Function to pause all audio/video
  const pauseAll = () => {
    if (videoRefs.current.length) {
      videoRefs.current.forEach((video) => video.pause());
    }
    if (audioRefs.current.length) {
      audioRefs.current.forEach((audio) => audio.pause());
    }
  };

  // Function to play specific audio/video by index
  const playByIndex = (index) => {
    if (currentState.itemType === "video" && videoRefs.current[index]) {
      videoRefs.current[index].play();
    }
    if (currentState.itemType === "audio" && audioRefs.current[index]) {
      audioRefs.current[index].play();
    }
  };

    // Function to handle sequential playback
  const handleSequentialPlayback = () => {
    console.log('handleSequentialPlayback called, current state:', sequentialPlayback);

    if (currentState.itemType !== "audio" && currentState.itemType !== "video") {
      console.log('Not audio/video, returning');
      return;
    }

    // Use callback to get current state
    setSequentialPlayback(prev => {
      console.log('Current sequential state in callback:', prev);

      // If sequential playback is active, stop it and save position
      if (prev.isActive) {
        console.log('Stopping sequential playback');

        // Save current position before stopping
        let currentPosition = 0;
        if (audioRefs.current[prev.currentIndex] && audioRefs.current[prev.currentIndex].currentTime) {
          currentPosition = audioRefs.current[prev.currentIndex].currentTime;
          console.log(`💾 Saving position ${currentPosition} for audio ${prev.currentIndex}`);
        }

        pauseAll();

        // Reset individual playback state when stopping sequential
        setIndividualPlayback({ activeIndex: -1, isPlaying: false });

        // Remove event listeners with proper cleanup
        if (audioRefs.current.length) {
          audioRefs.current.forEach(audio => {
            if (audio && audio._sequentialHandler) {
              audio.removeEventListener('ended', audio._sequentialHandler);
              delete audio._sequentialHandler;
            }
          });
        }
        return {
          isActive: false,
          currentIndex: prev.currentIndex, // Keep current index
          isPlaying: false,
          pausedPosition: currentPosition // Save position
        };
      }

                  // Start/resume sequential playback
      console.log('Starting/resuming sequential playback, audio refs length:', audioRefs.current.length);
      console.log('Previous state:', prev);

      // Reset individual playback state
      setIndividualPlayback({ activeIndex: -1, isPlaying: false });

      pauseAll(); // Stop any current playback

      // Determine which audio to play and from what position
      // If pausedPosition is 0, this is a fresh start, so always start from left (index 0)
      // Otherwise, resume from saved position
      const isNewStart = prev.pausedPosition === 0;
      const indexToPlay = isNewStart ? 0 : prev.currentIndex; // Fresh start = left audio, otherwise saved index
      const positionToPlay = prev.pausedPosition || 0; // Use saved position or start from beginning

      console.log(`▶️ ${isNewStart ? 'Starting fresh' : 'Resuming'} audio ${indexToPlay} from position ${positionToPlay}`);

      // Start/resume audio playback
      if (audioRefs.current[indexToPlay]) {
        console.log(`${isNewStart ? 'Starting fresh audio playback' : 'Resuming audio playback from saved position'}`);
        audioRefs.current[indexToPlay].currentTime = positionToPlay;
        audioRefs.current[indexToPlay].play().catch(e => console.error('Play failed:', e));
      } else {
        console.error('No audio ref found at index', indexToPlay);
      }

      // Set up the sequential cycle
      setTimeout(() => startSequentialCycle(), 100); // Slight delay to ensure state is updated

      return {
        isActive: true,
        currentIndex: indexToPlay,
        isPlaying: true,
        pausedPosition: 0 // Clear saved position since we're playing now
      };
     });
  };

          const startSequentialCycle = () => {
    console.log('startSequentialCycle called');
    console.log('currentState.itemType:', currentState.itemType);
    console.log('audioRefs.current.length:', audioRefs.current.length);

    // Set up event listeners for when audio ends
    if (currentState.itemType === "audio" && audioRefs.current.length >= 2) {
      console.log('Setting up event listeners for audio elements');

              audioRefs.current.forEach((audio, index) => {
        if (audio) {
          console.log(`🔧 Setting up listener for audio ${index}`);
          console.log(`🔧 Audio element duration: ${audio.duration || 'not loaded'}`);
          console.log(`🔧 Audio element src: ${audio.src}`);

          // Remove existing listeners to avoid duplicates
          if (audio._sequentialHandler) {
            audio.removeEventListener('ended', audio._sequentialHandler);
            delete audio._sequentialHandler;
          }

          // Add new listener with proper closure
          const handleEnded = () => {
            console.log(`🔔 Audio ${index} ENDED event fired! Duration: ${audio.duration}, Current time: ${audio.currentTime}`);
            handleAudioEnded(index);
          };

          audio.addEventListener('ended', handleEnded);
          // Store reference for cleanup
          audio._sequentialHandler = handleEnded;

                    // Add debugging for metadata loading
          audio.addEventListener('loadedmetadata', () => {
            console.log(`🔧 Audio ${index} metadata loaded, duration: ${audio.duration}`);
          });

          console.log(`🔧 Event listener set up for audio ${index}`);
        } else {
          console.log(`🔧 No audio element found at index ${index}`);
        }
      });
    } else {
      console.log('Not setting up event listeners - wrong type or not enough audio refs');
    }
  };

      const handleAudioEnded = (endedIndex) => {
    console.log(`🎵 handleAudioEnded called with index: ${endedIndex}`);
    console.log(`🎵 Audio refs length: ${audioRefs.current.length}`);
    console.log(`🎵 Audio elements:`, audioRefs.current.map((audio, i) => audio ? `${i}:exists` : `${i}:null`));

    setSequentialPlayback(prev => {
      console.log(`🎵 Current sequential state in handleAudioEnded:`, prev);
      if (!prev.isActive || !prev.isPlaying) {
        console.log(`🎵 Not switching - isActive: ${prev.isActive}, isPlaying: ${prev.isPlaying}`);
        return prev;
      }

      const nextIndex = endedIndex === 0 ? 1 : 0;
      console.log(`🎵 Switching from ${endedIndex} to ${nextIndex}`);

      // Pause the ended audio and play the next one
      if (audioRefs.current[endedIndex]) {
        console.log(`🎵 Pausing audio ${endedIndex}`);
        audioRefs.current[endedIndex].pause();
      }

      // Immediately play the next audio
      setTimeout(() => {
        console.log(`🎵 Playing audio at index ${nextIndex}`);
        if (audioRefs.current[nextIndex]) {
          console.log(`🎵 Found audio element at index ${nextIndex}, starting playback`);
          audioRefs.current[nextIndex].currentTime = 0; // Start from beginning
          audioRefs.current[nextIndex].play()
            .then(() => console.log(`🎵 Successfully started playing audio ${nextIndex}`))
            .catch(e => console.error(`🎵 Play failed for audio ${nextIndex}:`, e));
        } else {
          console.error(`🎵 No audio element found at index ${nextIndex}`);
        }
      }, 50);

      return {
        ...prev,
        currentIndex: nextIndex,
        isPlaying: true,
        pausedPosition: 0 // Clear saved position when transitioning
      };
    });
  };

          // Function for toggle player (T key)
  const togglePlayer = () => {
    console.log('togglePlayer called');

    setSequentialPlayback(prev => {
      console.log('togglePlayer callback, prev state:', prev);

      if (prev.isActive) {
        console.log('Toggling player, current playing state:', prev.isPlaying);
        console.log('Current index:', prev.currentIndex);
        console.log('Audio refs available:', audioRefs.current.length);

        const newPlayingState = !prev.isPlaying;

        if (newPlayingState) {
          // Resume playing from current position
          console.log('Resuming playback at index:', prev.currentIndex);
          if (audioRefs.current[prev.currentIndex]) {
            audioRefs.current[prev.currentIndex].play().catch(e => console.error('Resume play failed:', e));
          } else {
            console.error('No audio ref at current index for resume');
          }
        } else {
          // Pause current audio (preserves position)
          console.log('Pausing playback');
          if (audioRefs.current[prev.currentIndex]) {
            audioRefs.current[prev.currentIndex].pause();
          } else {
            console.error('No audio ref at current index for pause');
          }
        }

        return {
          ...prev,
          isPlaying: newPlayingState
        };
      } else {
        console.log('Sequential playback not active, T key ignored');
        return prev;
      }
    });
  };

          // Function for restart (R key)
  const restartPlayback = () => {
    console.log('restartPlayback called');

    setSequentialPlayback(prev => {
      console.log('restartPlayback callback, prev state:', prev);

      if (prev.isActive) {
        console.log('Restarting playback');
        console.log('Audio refs available:', audioRefs.current.length);

        // Stop all and restart from left audio at beginning
        pauseAll();
        if (audioRefs.current[0]) {
          console.log('Restarting left audio from beginning');
          audioRefs.current[0].currentTime = 0;
          audioRefs.current[0].play().catch(e => console.error('Restart play failed:', e));
        } else {
          console.error('No audio ref found at index 0 for restart');
        }

        return {
          ...prev,
          currentIndex: 0,
          isPlaying: true,
          pausedPosition: 0 // Clear saved position when restarting
        };
      } else {
        console.log('Sequential playback not active, R key ignored');
        return prev;
      }
    });
  };

  // Legacy function for backward compatibility with videos
  const togglePlayPause = () => {
    if (currentState.itemType === "audio") {
      handleSequentialPlayback();
    } else if (currentState.itemType === "video") {
      videoPlayback.togglePlayPause();
    }
  };

  // Add a new function to handle arrow key presses
  const handleKeyDown = (e) => {
    // Keep focused buttons and form controls native, but let documented survey
    // shortcuts win when the first-play overlay has focused a video.
    if (shouldIgnoreSurveyShortcut(e)) {
      return;
    }

    console.log('Key pressed:', e.code, e.key);

    // Only process if we have variants to select and we're not currently fetching
    if (currentState.files.length < 2 || !isSend || isFetchingNext) {
      console.log('Ignoring key press - not ready');
      return;
    }

    // Check both e.code and e.key for better compatibility
    if (e.code === "ArrowLeft" || e.key === "ArrowLeft") {
      e.preventDefault(); // Prevent scrolling and other default behaviors
      e.stopPropagation(); // Stop event from bubbling

      // Stop sequential playback if active
      if (sequentialPlayback.isActive) {
        setSequentialPlayback({ isActive: false, currentIndex: 0, isPlaying: false, pausedPosition: 0 });
        setIndividualPlayback({ activeIndex: -1, isPlaying: false });
        pauseAll();
        // Remove event listeners with proper cleanup
        if (audioRefs.current.length) {
          audioRefs.current.forEach(audio => {
            if (audio && audio._sequentialHandler) {
              audio.removeEventListener('ended', audio._sequentialHandler);
              delete audio._sequentialHandler;
            }
          });
        }
      }

      setKeyboardSelected(0); // Set visual feedback
      setCurrentState({ ...currentState, selection: 0 });
      if (isSend) handleNext(0);
    } else if (e.code === "ArrowRight" || e.key === "ArrowRight") {
      e.preventDefault(); // Prevent scrolling and other default behaviors
      e.stopPropagation(); // Stop event from bubbling

      // Stop sequential playback if active
      if (sequentialPlayback.isActive) {
        setSequentialPlayback({ isActive: false, currentIndex: 0, isPlaying: false, pausedPosition: 0 });
        setIndividualPlayback({ activeIndex: -1, isPlaying: false });
        pauseAll();
        // Remove event listeners with proper cleanup
        if (audioRefs.current.length) {
          audioRefs.current.forEach(audio => {
            if (audio && audio._sequentialHandler) {
              audio.removeEventListener('ended', audio._sequentialHandler);
              delete audio._sequentialHandler;
            }
          });
        }
      }

      setKeyboardSelected(1); // Set visual feedback
      setCurrentState({ ...currentState, selection: 1 });
      if (isSend) handleNext(1);
    } else if (e.code === "Space" || e.key === " ") {
      e.preventDefault(); // Prevent scrolling and other default behaviors
      e.stopPropagation(); // Stop event from bubbling
      togglePlayPause();
    } else if (e.code === "KeyT" || e.key === "t" || e.key === "T") {
      console.log('T key pressed');
      e.preventDefault();
      e.stopPropagation();
      togglePlayer();
    } else if (e.code === "KeyR" || e.key === "r" || e.key === "R") {
      console.log('R key pressed');
      e.preventDefault();
      e.stopPropagation();
      restartPlayback();
    }
  };

  // Look at the queued pairs too, so the slot is reserved before the first
  // prompted pair is reached rather than appearing partway through.
  useEffect(() => {
    if (surveyShowsPrompt) return;
    const hasPrompt = (item) => Boolean(item?.caption?.trim() || item?.caption_error);
    if (hasPrompt(currentState) || prefetchQueue.some(hasPrompt)) {
      setSurveyShowsPrompt(true);
    }
  }, [currentState, prefetchQueue, surveyShowsPrompt]);

  // Add effect to clear the keyboard selection visual feedback after a short delay
  useEffect(() => {
    if (keyboardSelected !== null) {
      const timer = setTimeout(() => {
        setKeyboardSelected(null);
      }, 500); // Clear after 500ms

      return () => clearTimeout(timer);
    }
  }, [keyboardSelected]);

  // Update the existing keydown event listener to include arrow keys
  useEffect(() => {
    const listener = (e) => handleKeyDown(e);
    document.addEventListener("keydown", listener, true);

    // Ensure document.body can receive focus for our focus management
    if (!document.body.hasAttribute('tabindex')) {
      document.body.setAttribute('tabindex', '-1');
    }

    return () => {
      document.removeEventListener("keydown", listener, true);
    };
  }, [
    currentState,
    isSend,
    isFetchingNext,
    sequentialPlayback,
    prefetchQueue,
    isPrefetching,
  ]); // Add dependencies to ensure the handler has access to latest state

  // Reset sequential playback when current state changes (new audio pair loaded)
  useEffect(() => {
    if (currentState.files.length > 0 && currentState.itemType === "audio") {
      // Reset sequential playback state for new audio pair
      setSequentialPlayback(prev => {
        if (prev.isActive) {
          console.log('🔄 New audio pair loaded, resetting sequential playback');
          pauseAll();
          // Clean up old event listeners
          if (audioRefs.current.length) {
            audioRefs.current.forEach(audio => {
              if (audio && audio._sequentialHandler) {
                audio.removeEventListener('ended', audio._sequentialHandler);
                delete audio._sequentialHandler;
              }
            });
          }
        }
        return { isActive: false, currentIndex: 0, isPlaying: false, pausedPosition: 0 };
      });

      // Also reset individual playback for new audio pair
      setIndividualPlayback({ activeIndex: -1, isPlaying: false });
    }
  }, [currentState.files, currentState.itemType]);

  // Cleanup sequential playback on component unmount
  useEffect(() => {
    return () => {
      // Remove event listeners when component unmounts
      if (audioRefs.current.length) {
        audioRefs.current.forEach(audio => {
          if (audio && audio._sequentialHandler) {
            audio.removeEventListener('ended', audio._sequentialHandler);
            delete audio._sequentialHandler;
          }
        });
      }
    };
  }, []);

    const sendVote = async (selectionIndex, voteType = 'winner_loser') => {
    const getScreenResolution = () => {
      // Get screen width and height using window object
      const width =
        window.innerWidth ||
        document.documentElement.clientWidth ||
        document.body.clientWidth;
      const height =
        window.innerHeight ||
        document.documentElement.clientHeight ||
        document.body.clientHeight;
      return `${width}x${height}`;
    };
    const method = voteDetails.current.undo ? "PUT" : "POST";
    let url = `${apiBaseUrl}vote`;
    if (voteDetails.current.undo) {
      url += `Update/${voteDetails.current.id}`;
      voteDetails.current = { ...voteDetails.current, undo: undefined };
    }

    setIsSend(true);

        if (voteType === 'tie') {
      // For tie votes, send one vote with both variants - backend will handle the tie logic
      const response = await authenticatedFetch(navigate, url, {
        method: method,
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          survey_id: surveyId,
          variant_id_winner: currentState.variantsID[0],
          variant_id_loser: currentState.variantsID[1],
          vote_type: voteType,
          media: currentState.mediaName,
          device: /Mobile/.test(navigator.userAgent) ? "Mobile" : "Desktop",
          screen_resolution: getScreenResolution(),
        }),
      });

      if (response.ok) {
        const resJson = await response.json();
        voteDetails.current = { ...voteDetails.current, id: resJson.vote_id };
      } else {
        const resJson = await response.json();
        setAlertMessage({
          type: "danger",
          header: "Error Recording Vote",
          msg: resJson.message || "Failed to record your tie vote.",
        });
      }
        } else if (voteType === 'equally_bad') {
      // For equally bad votes, send both variants - backend will handle the equally_bad logic
      const response = await authenticatedFetch(navigate, url, {
        method: method,
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          survey_id: surveyId,
          variant_id_winner: currentState.variantsID[0],
          variant_id_loser: currentState.variantsID[1],
          vote_type: voteType,
          media: currentState.mediaName,
          device: /Mobile/.test(navigator.userAgent) ? "Mobile" : "Desktop",
          screen_resolution: getScreenResolution(),
        }),
      });

      if (response.ok) {
        const resJson = await response.json();
        voteDetails.current = { ...voteDetails.current, id: resJson.vote_id };
      } else {
        const resJson = await response.json();
        setAlertMessage({
          type: "danger",
          header: "Error Recording Vote",
          msg: resJson.message || "Failed to record your equally bad vote.",
        });
      }
    } else {
      // For winner_loser votes, send single vote
      const variant_id_winner = currentState.variantsID[selectionIndex];
      const variant_id_loser = currentState.variantsID[1 - selectionIndex];

      const response = await authenticatedFetch(navigate, url, {
        method: method,
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          survey_id: surveyId,
          variant_id_winner: variant_id_winner,
          variant_id_loser: variant_id_loser,
          vote_type: voteType,
          media: currentState.mediaName,
          device: /Mobile/.test(navigator.userAgent) ? "Mobile" : "Desktop",
          screen_resolution: getScreenResolution(),
        }),
      });

      if (response.ok) {
        const resJson = await response.json();
        voteDetails.current = { ...voteDetails.current, id: resJson.vote_id };
      } else {
        const resJson = await response.json();
        setAlertMessage({
          type: "danger",
          header: "Error Recording Vote",
          msg: resJson.message || "Failed to record your vote.",
        });
      }
    }
  };

  // Modified handler for "Next" button click
  const handleNext = (selectionIndex, voteType = 'winner_loser') => {
    // Reset sequential playback when moving to next item
    if (sequentialPlayback.isActive) {
      setSequentialPlayback({ isActive: false, currentIndex: 0, isPlaying: false, pausedPosition: 0 });
      pauseAll();
      // Remove event listeners with proper cleanup
      if (audioRefs.current.length) {
        audioRefs.current.forEach(audio => {
          if (audio && audio._sequentialHandler) {
            audio.removeEventListener('ended', audio._sequentialHandler);
            delete audio._sequentialHandler;
          }
        });
      }
    }

    // Save the current state as previous
    setPrevState({ ...currentState, selection: selectionIndex });

    // If a selection was made, send the vote
    if (isSend && (selectionIndex !== "" && selectionIndex !== undefined || voteType !== 'winner_loser')) {
      setIsSend(false);
      sendVote(selectionIndex, voteType);
    }

    // Get the next item from the queue
    const nextItem = getNextFromQueue();

    if (nextItem) {
      // If we have a prefetched item, use it immediately
      // Reset selection to ensure clean state for new pair
      setCurrentState({ ...nextItem, selection: "" });
      setIsSend(true);
    } else {
      // If the queue is empty, fetch directly (fallback)
      setIsFetchingNext(true);
      fetchPair().then(newState => {
        if (newState) {
          // Ensure selection is reset (fetchPair already does this, but being explicit)
          setCurrentState({ ...newState, selection: "" });
          setIsSend(true);
        }
        setIsFetchingNext(false);
      });
    }

    // Ensure the prefetch queue stays filled
    fillPrefetchQueue();
  };

  const handleUndo = () => {
    // Push current state back to the front of the queue
    setPrefetchQueue(prevQueue => [currentState, ...prevQueue]);

    // Restore the previous state
    setCurrentState(prevState);
    setPrevState(emptyState);
    voteDetails.current = { ...voteDetails.current, undo: true };
  };

  // Custom loading screen with progress indicator
  const renderLoadingScreen = () => (
    <Loading message="Preparing Survey" />
  );

  return (
    <div className="text-center justify-content-center">
      {/* Show loading screen during initial loading */}
      {isInitialLoading ? (
        renderLoadingScreen()
      ) : !currentState.files.length && !alertMessage.msg ? (
        <Loading message="Loading Next Item" />
      ) : (
        <div>
          <Row className="h1 mb-2">
            <header>{surveyTitle}</header>
          </Row>

          {currentState.itemType === "video" && (
            <PlaybackModeToggle
              autoAlternate={videoPlayback.autoAlternate}
              hasStarted={videoPlayback.hasStarted}
              onToggle={videoPlayback.toggleAutoAlternate}
            />
          )}

          {surveyShowsPrompt && (
            <PromptPanel prompt={currentState.caption} error={currentState.caption_error} />
          )}

          <Container className="mb-5 justify-content-center d-flex flex-row">
            {currentState.files &&
              currentState.files.map((file, index) => {
                const viewIndex = index + 1;
                const isSelected = currentState.selection === index && isSend;
                const variantId = currentState.variantsID?.[index];
                const displayFields =
                  (currentState.variantDisplays || []).find((d) => d.variant_id === variantId)?.fields || [];
                const borderConfig = "border border-primary border-5";
                return (
                  <Container
                    key={index}
                    className={`me-2 ${keyboardSelected === index ? 'keyboard-selected' : ''}`}
                    style={isFetchingNext ? {pointerEvents: "none"} : { opacity: 1, pointerEvents: "auto" }}
                  >
                    {/* Render image or video based on item type */}
                    {currentState.itemType === "image" && (
                      <CustomImage
                        className={isSelected ? borderConfig : ""}
                        src={currentState.files[index]}
                        alt={`Item ${viewIndex}`}
                        fluid
                        rounded={2}
                        preload={"auto"}
                      />
                    )}
                    {currentState.itemType === "video" && (
                      <ComparisonVideo
                        ref={(el) => (videoRefs.current[index] = el)}
                        src={currentState.files[index]}
                        label={`Option ${viewIndex}`}
                        selectedClassName={isSelected ? borderConfig : ""}
                        hasStarted={videoPlayback.hasStarted}
                        isPlaying={videoPlayback.isPlaying(index)}
                        isAudible={videoPlayback.isAudible(index)}
                        onSoundToggle={videoPlayback.toggleSound}
                        onMouseEnter={() => videoPlayback.handleMouseEnter(index)}
                        onMouseLeave={() => videoPlayback.handleMouseLeave(index)}
                        onVideoClick={() => videoPlayback.handleClick(index)}
                        onVideoDoubleClick={() => videoPlayback.handleDoubleClick(index)}
                        onPlay={() => videoPlayback.handlePlay(index)}
                        onPause={() => videoPlayback.handlePause(index)}
                        onEnded={() => videoPlayback.handleEnded(index)}
                        onCanPlay={() => videoPlayback.handleCanPlay(index)}
                      />
                    )}
                    {currentState.itemType === "audio" && (
                      <WaveformPlayer
                        ref={(el) => {
                          // Store the audio element from the WaveformPlayer
                          if (el && el.audioRef && el.audioRef.current) {
                            audioRefs.current[index] = el.audioRef.current;
                          }
                        }}
                        src={currentState.files[index]}
                        isCurrentlyPlaying={isAudioPlaying(index)}
                        className={`${isSelected ? borderConfig : ''} ${sequentialPlayback.isActive && sequentialPlayback.currentIndex === index ? 'currently-playing' : ''}`}
                        disabled={false}
                        loop={!sequentialPlayback.isActive} // Disable loop during sequential playback
                        onPlay={() => {
                          console.log(`🎵 WaveformPlayer ${index} play event`);

                          if (sequentialPlayback.isActive) {
                            // During sequential playback, this should resume sequential playback
                            if (sequentialPlayback.currentIndex === index && !sequentialPlayback.isPlaying) {
                              console.log('🎵 Resuming sequential playback via button');
                              togglePlayer(); // Use existing T key functionality
                            }
                          } else {
                            // Individual playback mode
                            console.log(`🎵 Starting individual playback for audio ${index}`);

                            // Pause other audio first
                            audioRefs.current.forEach((audio, audioIndex) => {
                              if (audio && audioIndex !== index && !audio.paused) {
                                audio.pause();
                              }
                            });

                            // Update individual playback state
                            setIndividualPlayback({
                              activeIndex: index,
                              isPlaying: true
                            });
                          }
                        }}
                        onPause={() => {
                          console.log(`🎵 WaveformPlayer ${index} pause event`);

                          if (sequentialPlayback.isActive && sequentialPlayback.currentIndex === index) {
                            // During sequential playback, pause the sequential system
                            console.log('🎵 Pausing sequential playback via button');
                            togglePlayer(); // Use existing T key functionality
                          } else if (individualPlayback.activeIndex === index) {
                            // Individual playback mode
                            console.log(`🎵 Pausing individual playback for audio ${index}`);
                            setIndividualPlayback({
                              activeIndex: index,
                              isPlaying: false
                            });
                          }
                        }}
                        onEnded={() => {
                          console.log(`🎵 WaveformPlayer ${index} ended event`);
                          if (sequentialPlayback.isActive && sequentialPlayback.currentIndex === index) {
                            handleAudioEnded(index);
                          }
                        }}
                        onTimeUpdate={() => {
                          // Handle time updates if needed
                        }}
                      />
                    )}

                    {/* Add voting button for all item types */}
                    <div className="vote-button-container">
                      <button
                        className={`primary-vote-button ${isSelected ? 'selected' : ''} ${keyboardSelected === index ? 'keyboard-selected' : ''}`}
                        disabled={!isSend || isFetchingNext}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setCurrentState({ ...currentState, selection: index });
                          if (isSend) handleNext(index, 'winner_loser');
                        }}
                      >
                        <div className="vote-button-content">
                          <div className="vote-icon">
                            {isSelected ? (
                              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            ) : (
                              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                                <path d="M8 12H16M12 8V16" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                              </svg>
                            )}
                          </div>
                          <div className="vote-text">
                            <div className="vote-action">{isSelected ? 'Selected' : 'Choose'}</div>
                            <div className="vote-label">Option {viewIndex}</div>
                          </div>
                        </div>
                      </button>
                    </div>
                    <VariantDisplayFields fields={displayFields} />
                  </Container>
                );
              })}
          </Container>

          {/* Rest of your existing UI */}
          <Row className="mb-5 justify-content-center">
            {/* Tie button */}
            <Col xs="auto">
              <button
                className="secondary-vote-button"
                onClick={isSend ? () => handleNext(undefined, 'tie') : null}
                disabled={
                  !isSend ||
                  alertMessage.header === "Survey Unavailable" ||
                  isFetchingNext
                }
                title="Select when both options are equally good"
              >
                <span className="secondary-icon">🤝</span>
                <span>{isSend && !isFetchingNext ? "Tie" : "Loading..."}</span>
              </button>
            </Col>
            {/* Equally Bad button */}
            <Col xs="auto">
              <button
                className="secondary-vote-button"
                onClick={isSend ? () => handleNext(undefined, 'equally_bad') : null}
                disabled={
                  !isSend ||
                  alertMessage.header === "Survey Unavailable" ||
                  isFetchingNext
                }
                title="Select when both options are equally poor"
              >
                <span className="secondary-icon">👎</span>
                <span>{isSend && !isFetchingNext ? "Equally Bad" : "Loading..."}</span>
              </button>
            </Col>
          </Row>

          {/* Add keyboard instructions at the bottom */}
          <div className="instructions mb-3 mt-4">
            {currentState.itemType === "audio" ? (
              <>
                Use the buttons or keyboard arrows to vote: <span className="key">←</span>{" "}
                for left, <span className="key">→</span> for right
                <br />
                <span className="key">Space</span> for sequential play, <span className="key">T</span> to pause/resume, <span className="key">R</span> to restart
                <br />
                Click the waveform to seek, click play button to play/pause individual audio
              </>
            ) : (
              <>
                Use mouse or keyboard arrows to vote: <span className="key">←</span>{" "}
                for left, <span className="key">→</span> for right
                {currentState.itemType === "video" && (
                  <>
                    , <span className="key">Space</span> to play/pause
                    <br />
                    {videoPlayback.autoAlternate
                      ? "Auto-play is on: the videos play one after the other, and the speaker icon marks the one you hear"
                      : "Hover a video to play it, or turn on auto-play to hear them one after the other"}
                  </>
                )}
              </>
            )}
            <br />
            Use Tie when both options are equally good, or Equally Bad when both are equally poor
            <br />
            Please wait for the next pair to load before voting again
          </div>
        </div>
      )}

      {/* Alert message */}
      <Row sm={3} className="ms-2 position-fixed top-0 start-0 z-1000">
        {alertMessage.msg && (
          <Alert
            variant={alertMessage.type}
            className={`${alertMessage.msg && "alert-animate-enter"} ${
              alertAnimation && "alert-animate-exit"
            }`}
          >
            {alertMessage.header !== "Survey Unavailable" && (
              <div className="d-flex justify-content-end">
                <CloseButton
                  onClick={() => {
                    setAlertAnimation(true);
                    setTimeout(() => setAlertMessage(""), 300);
                    setAlertAnimation(false);
                  }}
                />
              </div>
            )}
            <Alert.Heading className="text-start">
              {alertMessage.header}
            </Alert.Heading>
            <p className="text-start">{alertMessage.msg}</p>
          </Alert>
        )}
      </Row>

      {/* Loading overlay */}
      {!isSend && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(255, 255, 255, 0.8)",
            zIndex: 999,
            pointerEvents: "none",
          }}
        >
          <Loading message="Submitting Vote" /> {/* Optional loading spinner */}
        </div>
      )}
    </div>
  );
};

export default Survey;
